import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavLayout } from '../hooks/useNavLayout';

function Modal({ children, onClose, title = "操作", isOpen = false, isFullScreen = false, size = 'xl', closeOnBackdrop = true }) {
  const { contentLeftClass, contentTopClass } = useNavLayout();
  // 阻止背景滚动
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 处理ESC键关闭
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // 如果模态框未打开，不渲染任何内容
  if (!isOpen) {
    return null;
  }

  // 处理背景点击关闭（可由 closeOnBackdrop 关闭）
  const handleBackdropClick = (e) => {
    // if (e.target === e.currentTarget) {
    //   onClose();
    // }
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (isFullScreen) {
    return createPortal(
      <div className={`fixed ${contentTopClass} bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 ${contentLeftClass}`}>
        <div className="flex h-full min-w-0 flex-col">
          {/* 顶部工具栏：标题居中，关闭按钮靠右 */}
          <div className="relative flex shrink-0 items-center border-b border-gray-200 bg-white px-12 py-2.5 dark:border-gray-700 dark:bg-gray-800 sm:px-14 sm:py-3">
            <h3
              className="mx-auto line-clamp-2 w-full max-w-4xl px-2 text-center text-base font-semibold leading-snug text-gray-900 dark:text-gray-100 sm:text-lg"
              title={typeof title === 'string' ? title : undefined}
            >
              {title}
            </h3>
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-2xl font-bold leading-none text-gray-400 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:right-4"
              onClick={onClose}
              aria-label="关闭"
            >
              ×
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            {children}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const sizeClass = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  }[size] || 'max-w-4xl';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 p-3 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-5 ${sizeClass} w-full max-h-[95vh] sm:max-h-[90vh] overflow-auto relative`}>
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate pr-2">{title}</h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none transition-colors flex-shrink-0"
            onClick={onClose}
            aria-label="关闭"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default Modal; 